import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ImportFromDeviceModal = ({ 
  isOpen = false,
  onClose = () => {},
  onImport = () => {},
  className = '' 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock device contacts
  const deviceContacts = [
    {
      id: 'device_1',
      name: 'Sarah Johnson',
      phone: '(555) 123-4567',
      email: 'sarah.johnson@email.com',
      avatar: 'https://randomuser.me/api/portraits/women/1.jpg'
    },
    {
      id: 'device_2',
      name: 'Michael Chen',
      phone: '(555) 234-5678',
      email: 'michael.chen@email.com',
      avatar: 'https://randomuser.me/api/portraits/men/2.jpg'
    },
    {
      id: 'device_3',
      name: 'Emily Rodriguez',
      phone: '(555) 345-6789',
      email: 'emily.rodriguez@email.com',
      avatar: 'https://randomuser.me/api/portraits/women/3.jpg'
    },
    {
      id: 'device_4',
      name: 'David Wilson',
      phone: '(555) 456-7890',
      email: 'david.wilson@email.com',
      avatar: 'https://randomuser.me/api/portraits/men/4.jpg'
    },
    {
      id: 'device_5',
      name: 'Lisa Thompson',
      phone: '(555) 567-8901',
      email: 'lisa.thompson@email.com',
      avatar: 'https://randomuser.me/api/portraits/women/5.jpg'
    }
  ];

  const filteredContacts = deviceContacts?.filter(contact =>
    contact?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    contact?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    contact?.phone?.includes(searchQuery)
  );

  const handleContactToggle = (contact) => {
    setSelectedContacts(prev => {
      const isSelected = prev?.some(c => c?.id === contact?.id);
      if (isSelected) {
        return prev?.filter(c => c?.id !== contact?.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts?.length === filteredContacts?.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts([...filteredContacts]);
    }
  };

  const handleImport = async () => {
    if (selectedContacts?.length === 0) return;
    
    setIsLoading(true);
    
    // Simulate import process
    setTimeout(() => {
      onImport(selectedContacts);
      setIsLoading(false);
      onClose();
      setSelectedContacts([]);
      setSearchQuery('');
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-500 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className={`bg-surface rounded-lg shadow-modal max-w-md w-full max-h-[80vh] flex flex-col ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">
            Import from Device
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border">
          <Input
            type="search"
            placeholder="Search device contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Select All */}
        <div className="p-4 border-b border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            iconName={selectedContacts?.length === filteredContacts?.length ? "CheckSquare" : "Square"}
            iconPosition="left"
            className="w-full justify-start"
          >
            {selectedContacts?.length === filteredContacts?.length ? 'Deselect All' : 'Select All'}
            {filteredContacts?.length > 0 && (
              <span className="ml-auto text-text-secondary">
                ({selectedContacts?.length}/{filteredContacts?.length})
              </span>
            )}
          </Button>
        </div>

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto">
          {filteredContacts?.length > 0 ? (
            <div className="space-y-1 p-2">
              {filteredContacts?.map((contact) => {
                const isSelected = selectedContacts?.some(c => c?.id === contact?.id);
                return (
                  <button
                    key={contact?.id}
                    onClick={() => handleContactToggle(contact)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-micro hover:bg-muted ${
                      isSelected ? 'bg-primary/10 border border-primary/20' : ''
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <Icon 
                        name={isSelected ? "CheckSquare" : "Square"} 
                        size={20} 
                        className={isSelected ? 'text-primary' : 'text-text-secondary'} 
                      />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-muted overflow-hidden flex-shrink-0">
                      {contact?.avatar ? (
                        <img 
                          src={contact?.avatar} 
                          alt={contact?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="User" size={20} className="text-text-secondary" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="font-medium text-text-primary truncate">
                        {contact?.name}
                      </div>
                      <div className="text-sm text-text-secondary truncate">
                        {contact?.email || contact?.phone}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Icon name="Search" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
              <p className="text-text-secondary">
                {searchQuery ? 'No contacts found matching your search' : 'No device contacts available'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleImport}
              loading={isLoading}
              disabled={selectedContacts?.length === 0}
              className="flex-1"
            >
              {isLoading ? 'Importing...' : `Import ${selectedContacts?.length} Contact${selectedContacts?.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportFromDeviceModal;